/**
 * v3.5.20 — Client-grade Survey Results dashboard.
 *
 * Mirrors `pages/ReviewShieldResults.jsx` layout but scoped to a single survey:
 *   • Hero header with survey name + date-window selector + CSV export + cleanup
 *   • 4 stat cards (Total all-time / Avg score / Low ratings / Top language)
 *   • 4 charts via recharts: Satisfaction trend · Rating distribution
 *                            · Volume · Language breakdown
 *   • Per-question score breakdown (clients want to know which question dragged
 *     the average down — UUID-only view in v1 was useless to them)
 *   • Filterable & searchable response table with priority + score badges
 *   • Bulk delete + age-based cleanup
 *
 * Backend endpoints (all under /api/surveys/{id}):
 *   GET  /analytics?since_days=N            → all chart data + stat cards
 *   GET  /responses?limit=&skip=            → paginated rows for the table
 *   GET  /responses/export.csv              → flat CSV download
 *   POST /responses/cleanup?older_than_days → delete old/all responses
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Star, AlertTriangle, Download, Trash2, Loader2,
    BarChart3, MessageSquareText, Globe2, Search, Filter,
} from 'lucide-react';
import {
    AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';

import { surveysAPI } from '../api';
import { parseUtc } from '../../../lib/datetime';

const COLORS = {
    emerald: '#10b981', amber: '#f59e0b', red: '#ef4444',
    blue: '#3b82f6', purple: '#8b5cf6', orange: '#f97316', slate: '#64748b',
};

// 1★ red → 5★ green, matches Review Shield Results palette.
const RATING_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981'];
const LANGUAGE_COLORS = [COLORS.blue, COLORS.purple, COLORS.orange, COLORS.emerald, COLORS.slate];

const PriorityBadge = ({ priority }) => {
    if (!priority) return <Badge variant="secondary" className="text-[10px]">—</Badge>;
    const map = {
        HIGH: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800',
        MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
        LOW: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800',
    };
    return (
        <Badge className={`${map[priority] || ''} border text-[10px]`}>
            <AlertTriangle className="w-3 h-3 mr-1" />
            {priority}
        </Badge>
    );
};

// Authenticated respondent-photo thumbnail. Fetches the image as a blob via
// the org-scoped serve endpoint (never a public URL) and renders an object URL.
const ResponsePhoto = ({ surveyId, responseId, photoId }) => {
    const [url, setUrl] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        let active = true;
        let objUrl;
        surveysAPI.responsePhotoBlob(surveyId, responseId, photoId)
            .then((blob) => { if (active) { objUrl = URL.createObjectURL(blob); setUrl(objUrl); } })
            .catch(() => { if (active) setErr(true); });
        return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
    }, [surveyId, responseId, photoId]);
    if (err) return null;
    if (!url) return <div className="w-20 h-20 rounded-md bg-muted animate-pulse" data-testid={`response-photo-loading-${photoId}`} />;
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`response-photo-${photoId}`}>
            <img src={url} alt="" className="w-20 h-20 object-cover rounded-md border hover:opacity-90 transition-opacity" />
        </a>
    );
};


const RatingPill = ({ score }) => {
    if (score == null) return <span className="text-muted-foreground">—</span>;
    const idx = Math.max(0, Math.min(4, Math.round(score) - 1));
    const color = RATING_COLORS[idx];
    return (
        <span className="inline-flex items-center gap-1 font-semibold" style={{ color }}>
            <Star className="w-3.5 h-3.5 fill-current" />
            {Number(score).toFixed(2)}
        </span>
    );
};

const StatCard = ({ label, value, hint, accent = 'slate', testId }) => {
    const ring = {
        slate: 'border-slate-200 dark:border-slate-800',
        emerald: 'border-emerald-200 dark:border-emerald-900',
        red: 'border-red-200 dark:border-red-900',
        amber: 'border-amber-200 dark:border-amber-900',
        blue: 'border-blue-200 dark:border-blue-900',
    }[accent];
    const text = {
        slate: '', emerald: 'text-emerald-600 dark:text-emerald-400',
        red: 'text-red-600 dark:text-red-400',
        amber: 'text-amber-600 dark:text-amber-400',
        blue: 'text-blue-600 dark:text-blue-400',
    }[accent];
    return (
        <Card className={`border ${ring}`} data-testid={testId}>
            <CardContent className="pt-5 pb-4">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
                <p className={`text-3xl font-bold mt-1 ${text}`}>{value}</p>
                {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
            </CardContent>
        </Card>
    );
};

const ChartShell = ({ icon: Icon, title, children }) => (
    <Card>
        <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Icon className="w-4 h-4 text-muted-foreground" />
                {title}
            </CardTitle>
        </CardHeader>
        <CardContent className="h-[230px]">{children}</CardContent>
    </Card>
);

const NoData = ({ msg }) => (
    <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
        <BarChart3 className="w-7 h-7 opacity-30 mb-2" />
        <p className="text-xs">{msg}</p>
    </div>
);

const fmtDate = (s) => parseUtc(s).toLocaleString();

const SurveyResponses = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    const [analytics, setAnalytics] = useState(null);
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sinceDays, setSinceDays] = useState(30);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterLanguage, setFilterLanguage] = useState('all');
    const [exporting, setExporting] = useState(false);
    const [cleanupOpen, setCleanupOpen] = useState(false);
    const [cleanupDays, setCleanupDays] = useState('30');
    const [cleaning, setCleaning] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            const [a, r] = await Promise.all([
                surveysAPI.analytics(id, { sinceDays }),
                surveysAPI.listResponses(id, { limit: 200 }),
            ]);
            setAnalytics(a);
            setResponses(r.responses || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.loadFailed', 'Failed to load survey data'));
            navigate('/surveys');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id, sinceDays]);

    const surveyTitle = useMemo(() => {
        const n = analytics?.survey_name || {};
        return n[i18n.language] || n.en || n.fr || n.es || t('surveys.results.title', 'Survey Results');
    }, [analytics, i18n.language, t]);

    // Question text lookup so the table can show real text instead of UUIDs.
    const qidToText = useMemo(() => {
        const map = {};
        (analytics?.per_question || []).forEach(q => { map[q.question_id] = q.question_text; });
        responses.forEach(r => {
            (r.answers || []).forEach(a => {
                if (!map[a.question_id]) map[a.question_id] = a.question_text;
            });
        });
        return map;
    }, [analytics, responses]);

    // ── Chart data shaping ──
    const ratingDistData = useMemo(() => {
        const rd = analytics?.rating_distribution || {};
        return [1, 2, 3, 4, 5].map(s => ({ rating: `${s}★`, count: rd[s] || 0, color: RATING_COLORS[s - 1] }));
    }, [analytics]);

    const trendData = useMemo(() => analytics?.by_day || [], [analytics]);

    const languageData = useMemo(() => {
        const arr = analytics?.by_language || [];
        const total = arr.reduce((s, x) => s + x.count, 0);
        return arr.map((x, i) => ({
            name: x.language?.toUpperCase() || 'UNK',
            value: x.count,
            pct: total ? Math.round((x.count / total) * 100) : 0,
            color: LANGUAGE_COLORS[i % LANGUAGE_COLORS.length],
        }));
    }, [analytics]);

    const filteredResponses = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return responses.filter(r => {
            if (filterPriority !== 'all' && (r.priority || 'NONE') !== filterPriority) return false;
            const lang = r.respondent_meta?.language || r.language || '';
            if (filterLanguage !== 'all' && lang !== filterLanguage) return false;
            if (term) {
                const blob = [
                    r.notes || '',
                    ...(r.answers || []).map(a => `${a.question_text || ''} ${a.value}`),
                ].join(' ').toLowerCase();
                if (!blob.includes(term)) return false;
            }
            return true;
        });
    }, [responses, searchTerm, filterPriority, filterLanguage]);

    const distinctLanguages = useMemo(() => {
        const s = new Set(responses.map(r => r.respondent_meta?.language || r.language).filter(Boolean));
        return Array.from(s);
    }, [responses]);

    const handleExport = async () => {
        try {
            setExporting(true);
            const blob = await surveysAPI.exportResponsesCsv(id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safe = (analytics?.survey_name?.en || 'survey').replace(/\s+/g, '_').slice(0, 60);
            a.download = `${safe}_responses.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t('surveys.results.exported', 'CSV downloaded'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.results.exportFailed', 'Export failed'));
        } finally {
            setExporting(false);
        }
    };

    const handleCleanup = async () => {
        try {
            setCleaning(true);
            const res = await surveysAPI.cleanupResponses(id, parseInt(cleanupDays, 10));
            toast.success(t('surveys.results.cleanupDone', { count: res.deleted, defaultValue: '{{count}} responses removed' }));
            setCleanupOpen(false);
            loadData();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.results.cleanupFailed', 'Cleanup failed'));
        } finally {
            setCleaning(false);
        }
    };

    if (loading && !analytics) {
        return (
            <div className="h-[60vh] flex items-center justify-center" data-testid="survey-results-loading">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const total = analytics?.total_all_time ?? 0;
    const avg = analytics?.avg_score;
    const lows = analytics?.low_ratings ?? 0;
    const topLang = languageData.length ? languageData.reduce((a, b) => a.value > b.value ? a : b).name : '—';

    return (
        <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto" data-testid="survey-responses-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/surveys')} data-testid="back-to-surveys">
                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                        {t('common.back', 'Back')}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold leading-tight">{surveyTitle}</h1>
                        <p className="text-xs text-muted-foreground">
                            {t('surveys.results.subtitle', 'Guest feedback collected through this survey')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(parseInt(v, 10))}>
                        <SelectTrigger className="w-[150px]" data-testid="since-days-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">{t('surveys.results.last7', 'Last 7 days')}</SelectItem>
                            <SelectItem value="30">{t('surveys.results.last30', 'Last 30 days')}</SelectItem>
                            <SelectItem value="90">{t('surveys.results.last90', 'Last 90 days')}</SelectItem>
                            <SelectItem value="365">{t('surveys.results.last365', 'Last 12 months')}</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline" size="sm"
                        onClick={handleExport} disabled={exporting || !total}
                        data-testid="export-csv-btn"
                    >
                        {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                        {t('surveys.results.exportCsv', 'Export CSV')}
                    </Button>
                    <Button
                        variant="outline" size="sm" className="text-red-600 hover:text-red-700"
                        onClick={() => setCleanupOpen(true)} disabled={!total}
                        data-testid="cleanup-btn"
                    >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        {t('surveys.results.cleanup', 'Cleanup')}
                    </Button>
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                    label={t('surveys.results.totalAllTime', 'Total responses')}
                    value={total}
                    hint={total !== analytics?.total_responses ? t('surveys.results.windowHint', { count: analytics?.total_responses, defaultValue: '{{count}} in window' }) : null}
                    accent="slate"
                    testId="stat-total"
                />
                <StatCard
                    label={t('surveys.results.avgScore', 'Avg. score')}
                    value={avg != null ? `${avg.toFixed(2)} /5` : '—'}
                    accent="emerald"
                    testId="stat-avg"
                />
                <StatCard
                    label={t('surveys.results.lowRatings', 'Low ratings (≤2)')}
                    value={lows}
                    accent="red"
                    testId="stat-low"
                />
                <StatCard
                    label={t('surveys.results.topLang', 'Top language')}
                    value={topLang}
                    accent="blue"
                    testId="stat-top-lang"
                />
            </div>

            {/* Analytics row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ChartShell icon={Star} title={t('surveys.results.satisfactionTrend', 'Satisfaction Trend')}>
                    {trendData.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis domain={[1, 5]} tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Line type="monotone" dataKey="avg_score" stroke={COLORS.emerald} strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : <NoData msg={t('surveys.results.noTrend', 'No data in this window yet')} />}
                </ChartShell>

                <ChartShell icon={BarChart3} title={t('surveys.results.ratingDist', 'Rating Distribution')}>
                    {ratingDistData.some(d => d.count > 0) ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ratingDistData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="rating" tick={{ fontSize: 11 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {ratingDistData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <NoData msg={t('surveys.results.noRatings', 'No ratings yet')} />}
                </ChartShell>

                <ChartShell icon={MessageSquareText} title={t('surveys.results.volume', 'Feedback Volume')}>
                    {trendData.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Area type="monotone" dataKey="count" stroke={COLORS.blue} fill={COLORS.blue} fillOpacity={0.25} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : <NoData msg={t('surveys.results.noVolume', 'No responses in this window')} />}
                </ChartShell>

                <ChartShell icon={Globe2} title={t('surveys.results.languageBreakdown', 'Language Breakdown')}>
                    {languageData.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={languageData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                                    {languageData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" iconSize={8} formatter={(v, e) => `${v} (${e?.payload?.pct ?? 0}%)`} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : <NoData msg={t('surveys.results.noLang', 'No language data yet')} />}
                </ChartShell>
            </div>

            {/* Per-question score breakdown */}
            {(analytics?.per_question || []).length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">
                            {t('surveys.results.perQuestion', 'Score by question')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {analytics.per_question.map(q => {
                                const pct = q.avg ? (q.avg / 5) * 100 : 0;
                                const color = q.avg >= 4 ? COLORS.emerald : q.avg >= 3 ? COLORS.amber : COLORS.red;
                                return (
                                    <div key={q.question_id} className="flex items-center gap-3" data-testid={`pq-${q.question_id}`}>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm truncate" title={q.question_text}>{q.question_text}</p>
                                            <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full mt-1 overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0 w-24">
                                            <RatingPill score={q.avg} />
                                            <p className="text-[10px] text-muted-foreground">{q.count} {t('surveys.results.votes', 'votes')}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filterable response table */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Filter className="w-4 h-4 text-muted-foreground" />
                        {t('surveys.results.allResponses', 'All responses')}
                        <Badge variant="secondary" className="ml-1 text-[10px]">{filteredResponses.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Filter row */}
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder={t('surveys.results.searchPlaceholder', 'Search notes / answers')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8"
                                data-testid="results-search"
                            />
                        </div>
                        <Select value={filterPriority} onValueChange={setFilterPriority}>
                            <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-priority">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('surveys.results.allPriority', 'All priorities')}</SelectItem>
                                <SelectItem value="HIGH">HIGH</SelectItem>
                                <SelectItem value="MEDIUM">MEDIUM</SelectItem>
                                <SelectItem value="LOW">LOW</SelectItem>
                                <SelectItem value="NONE">{t('surveys.results.priorityNone', 'No priority')}</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterLanguage} onValueChange={setFilterLanguage}>
                            <SelectTrigger className="w-full sm:w-[140px]" data-testid="filter-language">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('surveys.results.allLanguages', 'All languages')}</SelectItem>
                                {distinctLanguages.map(l => (
                                    <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {filteredResponses.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-12 text-center">
                            {t('surveys.results.noResponses', 'No responses match your filters yet.')}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {filteredResponses.map(r => (
                                <div key={r.id} className="border rounded-lg p-3 space-y-2" data-testid={`response-row-${r.id}`}>
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-3 text-sm">
                                            <RatingPill score={r.score} />
                                            <PriorityBadge priority={r.priority} />
                                            {(r.respondent_meta?.language || r.language) && (
                                                <Badge variant="outline" className="text-[10px]">
                                                    {(r.respondent_meta?.language || r.language).toUpperCase()}
                                                </Badge>
                                            )}
                                            {r.reward_prize_title && (
                                                <Badge className="text-[10px] bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800" data-testid={`response-prize-${r.id}`}>
                                                    {t('surveys.results.prize', 'Prize')}: {r.reward_prize_title}
                                                </Badge>
                                            )}
                                        </div>
                                        <span className="text-xs text-muted-foreground">{fmtDate(r.submitted_at)}</span>
                                    </div>
                                    <ul className="text-sm space-y-1 ml-1">
                                        {(r.answers || []).map((a, i) => (
                                            <li key={i} className="flex gap-2 text-muted-foreground">
                                                <span className="text-foreground font-medium">
                                                    {a.question_text || qidToText[a.question_id] || a.question_id}:
                                                </span>
                                                <span>{a.other_text ? `${t('surveys.results.other', 'Other')}: ${a.other_text}` : String(a.value)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {/* P_MULTIPLE_CHOICE_BRANCHING_SURVEYS_1.0 — Branch Path summary.
                                        Renders the chain of branching decisions taken by this respondent,
                                        e.g. "Location → Laval → Service → Dine-In". Only appears when
                                        the response carries at least one branch_taken record. */}
                                    {(() => {
                                        const branchAnswers = (r.answers || []).filter(
                                            (a) => a?.branch_taken && a.branch_taken.choice_label_en
                                        );
                                        if (branchAnswers.length === 0) return null;
                                        return (
                                            <p
                                                className="text-xs text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-1 flex-wrap"
                                                data-testid={`response-branch-path-${r.id}`}
                                                data-insight-id="surveys.results.branch-path"
                                                data-insight-label="Branch path"
                                            >
                                                <span className="font-medium">
                                                    {t('surveys.results.branchPath', 'Branch Path')}:
                                                </span>
                                                {branchAnswers.map((a, i) => (
                                                    <span key={i}>
                                                        {a.branch_taken.choice_label_en}
                                                        {i < branchAnswers.length - 1 && (
                                                            <span className="mx-1 text-muted-foreground">→</span>
                                                        )}
                                                    </span>
                                                ))}
                                            </p>
                                        );
                                    })()}
                                    {r.notes && (
                                        <p className="text-sm italic text-muted-foreground border-l-2 border-fuchsia-300 pl-2">
                                            "{r.notes}"
                                        </p>
                                    )}
                                    {(r.photos || []).length > 0 && (
                                        <div className="flex flex-wrap gap-2 pt-1" data-testid={`response-photos-${r.id}`}>
                                            {(r.photos || []).map((p) => (
                                                <ResponsePhoto key={p.id} surveyId={id} responseId={r.id} photoId={p.id} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Cleanup dialog */}
            <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
                <DialogContent data-testid="cleanup-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('surveys.results.cleanupTitle', 'Clean up old responses')}</DialogTitle>
                        <DialogDescription>
                            {t('surveys.results.cleanupDesc', 'Permanently delete responses older than the selected window. This cannot be undone.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Select value={cleanupDays} onValueChange={setCleanupDays}>
                            <SelectTrigger data-testid="cleanup-days">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="30">{t('surveys.results.olderThan30', 'Older than 30 days')}</SelectItem>
                                <SelectItem value="90">{t('surveys.results.olderThan90', 'Older than 90 days')}</SelectItem>
                                <SelectItem value="180">{t('surveys.results.olderThan180', 'Older than 6 months')}</SelectItem>
                                <SelectItem value="365">{t('surveys.results.olderThan365', 'Older than 1 year')}</SelectItem>
                                <SelectItem value="0">{t('surveys.results.deleteAll', 'Delete ALL responses')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCleanupOpen(false)} disabled={cleaning}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button variant="destructive" onClick={handleCleanup} disabled={cleaning} data-testid="cleanup-confirm">
                            {cleaning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                            {t('surveys.results.confirmCleanup', 'Delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SurveyResponses;
